import clsx from 'clsx';
import type * as React from 'react';

import { Skeleton } from '../Skeleton';

/** Corner radius per {@link IconStackShape}. */
const SHAPE_CLASS: Record<IconStackShape, string> = {
    circle: 'rounded-full',
    rounded: 'rounded-sm',
};

export interface IconStackItem {
    key: string;
    icon: React.ReactNode;
}

export interface IconStackProps {
    /** Items to display in the stack. Each item needs a unique key and a rendered icon node. */
    items: IconStackItem[];
    /** Max number of icons to display. Defaults to 3. */
    max?: number;
    /** Tailwind size class for each container (e.g. 'size-4', 'size-5'). Defaults to 'size-5'. */
    size?: string;
    /** Additional className applied to each icon container. */
    className?: string;
    /**
     * Shape of each container. `rounded` matches content icons (spaces, sites), so an icon's box
     * and the stack read as the same element.
     * @default 'circle'
     */
    shape?: IconStackShape;
}

/** Shape of an {@link IconStack}'s containers. */
export type IconStackShape = 'circle' | 'rounded';

/**
 * A row of overlapping icon containers, circular or rounded (see `shape`). Content-agnostic:
 * callers provide pre-rendered nodes (icons, avatars, images, …) via `items`.
 *
 * Each item's border reads `--current-background` so the rings camouflage
 * with whatever surface the nearest parent is currently painting (e.g. a
 * focused `MenuItem`). See `Recipes/Patterns/Cascade currentBackground to
 * children` for the full contract. The `var(--neutral-surface)` fallback is
 * a safety net — the root token already publishes `--neutral-surface`, so
 * the fallback only fires if an ancestor unsets the variable.
 *
 * Z-indexes ascend left-to-right so the **rightmost** item paints on top.
 */
export function IconStack(props: IconStackProps): React.ReactElement {
    const { items, max = 3, size = 'size-5', className, shape = 'circle' } = props;
    const displayItems = items.slice(0, max);

    return (
        <div className="flex items-center">
            {displayItems.map((item, index) => (
                <div
                    key={item.key}
                    className={clsx(
                        'flex shrink-0 items-center justify-center overflow-hidden border border-[var(--current-background,var(--neutral-surface))] bg-neutral-ui',
                        SHAPE_CLASS[shape],
                        size,
                        index !== displayItems.length - 1 && '-mr-1',
                        className
                    )}
                    style={{ zIndex: index }}
                >
                    {item.icon}
                </div>
            ))}
        </div>
    );
}

export interface IconStackSkeletonProps {
    /** Number of skeleton placeholders to display. */
    count: number;
    /** Tailwind size class for each placeholder (e.g. 'size-4', 'size-5'). Defaults to 'size-5'. */
    size?: string;
    /** Additional className applied to each skeleton placeholder. */
    className?: string;
    /**
     * Shape of each placeholder, matching the {@link IconStack} it stands in for.
     * @default 'circle'
     */
    shape?: IconStackShape;
}

/**
 * Skeleton loading state for IconStack. Renders overlapping skeleton placeholders
 * with the same chrome and `shape` as the real `IconStack` items, so layouts
 * don't jump between loading and loaded states.
 */
export function IconStackSkeleton(props: IconStackSkeletonProps): React.ReactElement {
    const { count, size = 'size-5', className, shape = 'circle' } = props;

    return (
        <div className="flex items-center">
            {Array.from({ length: count }, (_, i) => `icon-skeleton-${i}`).map((key, index) => (
                <Skeleton
                    key={key}
                    className={clsx(
                        'shrink-0 border border-[var(--current-background,var(--neutral-surface))]',
                        SHAPE_CLASS[shape],
                        size,
                        index !== count - 1 && '-mr-1',
                        className
                    )}
                />
            ))}
        </div>
    );
}
